/**
 * Раздел «Экспертиза» (ЭКС-1a/1b): замечания экспертизы и разрешения на изменения.
 * Вкладки: «Замечания» (таблица с фильтрами, поиском, правкой карточки, ссылкой на
 * карточку листа и переключателем «показать черновики»), «Разрешения на изменения»
 * (CRUD), «Статистика» (expertise.stats + сопоставление ФИО). Исполнительные листы —
 * отдельный раздел /projects/sheets; импорт из 1С — CLI (вкладки убраны в ЭКС-1b-1).
 * Данные: /api/expertise (backend/app/api/expertise.py).
 */
import { api } from '../api.js';
import { canDo } from '../auth.js';
import { createDataTable } from '../components/data-table.js';
import { showNotification } from '../components/notification.js';
import { pickCounterparty, pickSheet } from '../components/ref-picker.js';
import { mountRecordPanel, openRecordPanel, closeRecordPanel } from '../components/record-panel.js';
import { router } from '../router.js';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const fmtDate = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('.') : '—');
const apiErrorText = (e) => e?.detail ?? e?.message ?? 'Ошибка';
const clip = (s) => `<span class="dt-clip" title="${esc(s ?? '')}">${esc(s || '—')}</span>`;

const TABS = [
  { key: 'remarks', label: 'Замечания' },
  { key: 'permits', label: 'Разрешения на изменения' },
  { key: 'stats', label: 'Статистика', need: 'expertise.stats' },
];

const CHANGE_CODE_LABEL = {
  error_fix: 'Устранение ошибок',
  extra_requirements: 'Доп. требования',
  other: 'Другое',
};

let _root = null;
let _mode = 'remarks';
let _table = null;
let _sections = null;   // кэш справочника разделов [{id, name, code}]
let _users = null;      // кэш пользователей для привязки ФИО [{id, full_name}]
let _showDrafts = false; // «показать черновики» во вкладке «Замечания»
let _bucket = 'current'; // ЭКС-1c: под-вкладка «Замечания» (current|archived_ours|external)

// ЭКС-1c: под-вкладки «Замечания». Текущие/Архивные наши — по закрытости ИЛ (is_actual);
// Архивные сторонние — чужие замечания без ИЛ (source='external'), не входят в статистику.
const REMARK_BUCKETS = [
  { key: 'current', label: 'Текущие' },
  { key: 'archived_ours', label: 'Архивные наши' },
  { key: 'external', label: 'Архивные сторонние' },
];

export async function initExpertise(container) {
  _table = null;
  container.innerHTML = `
    <div class="expertise-view tenders-view">
      <header class="tenders-header">
        <h2>Экспертиза</h2>
        <div class="tenders-modes" role="tablist">
          ${TABS.filter(t => !t.need || canDo(t.need))
            .map(t => `<button type="button" role="tab" class="tenders-mode" data-exp-tab="${t.key}">${t.label}</button>`).join('')}
        </div>
        <span class="tenders-hint">Замечания перенесены из 1С «ПКБ ТИТАН(Б)»; новые приходят файлом в карточку <a class="dt-link" href="/projects/sheets" data-route="/projects/sheets">исполнительного листа</a>.</span>
      </header>
      <div class="expertise-body tenders-body"></div>
    </div>`;
  _root = container.querySelector('.expertise-view');
  // Открытая правая панель записи (УИ-2a) сжимает таблицу, а не закрывает её
  // (переиспользуем --tdi-space из tenders.css, как tenders.js:76).
  _root.addEventListener('ccpm:inspector-resize', (e) => {
    _root.style.setProperty('--tdi-space', `${Math.round(e.detail.width)}px`);
  });
  mountRecordPanel(_root, { key: 'expertise', defaultWidth: 520 });
  _root.addEventListener('click', (e) => {
    const t = e.target.closest?.('[data-exp-tab]');
    if (t) { e.preventDefault(); setMode(t.dataset.expTab); }
  });
  setMode('remarks', true);
}

function setMode(mode, force = false) {
  if (mode === _mode && !force && _root.querySelector('.expertise-body').children.length) return;
  closeRecordPanel();   // несохранённые правки при смене вкладки закрываем молча (УИ-2a)
  _mode = mode;
  _table = null;
  _root.querySelectorAll('.tenders-mode').forEach(b => {
    b.classList.toggle('is-active', b.dataset.expTab === mode);
    b.setAttribute('aria-selected', String(b.dataset.expTab === mode));
  });
  const body = _root.querySelector('.expertise-body');
  body.innerHTML = '';
  if (mode === 'remarks') renderRemarks(body);
  else if (mode === 'permits') renderPermits(body);
  else renderStats(body);
}

async function loadSections() {
  if (_sections) return _sections;
  try { _sections = await api.expertise.sections(); } catch { _sections = []; }
  return _sections;
}

async function loadUsers() {
  if (_users) return _users;
  try { _users = await api.expertise.usersForLink(); } catch { _users = []; }
  return _users;
}

/** Опции select пользователя: «— не связан», затем ФИО (выбор задаётся через value поля). */
function userOptions() {
  return [{ value: '', label: '— не связан' }].concat(
    (_users ?? []).map(u => ({ value: String(u.id), label: u.full_name })),
  );
}

// ── Вкладка «Замечания» ───────────────────────────────────────────────────────

const REMARK_COLUMNS = [
  { key: 'remark_no', label: '№', width: 56, align: 'right', sortKey: 'remark_no', render: r => esc(r.remark_no ?? '—') },
  { key: 'sheet_short_name', label: 'Лист', width: 220,
    render: r => (r.project_sheet_id
      ? `<a class="dt-link" data-route="/projects/sheets?id=${r.project_sheet_id}" title="${esc(r.sheet_short_name ?? '')}">${esc(r.sheet_short_name || ('лист #' + r.project_sheet_id))}</a>`
      : clip(r.sheet_short_name)) },
  { key: 'section_name', label: 'Раздел', width: 150, render: r => clip(r.section_name || r.section_raw) },
  { key: 'expertise_org', label: 'Экспертиза', width: 160, render: r => clip(r.expertise_org) },
  { key: 'expert', label: 'Эксперт', width: 140, render: r => clip(r.expert) },
  { key: 'remark_text', label: 'Замечание', width: 340, render: r => clip(r.remark_text) },
  { key: 'answer_text', label: 'Ответ', width: 260, render: r => clip(r.answer_text) },
  { key: 'responsible', label: 'Ответственный', width: 150, render: r => clip(r.responsible) },
  { key: 'is_closed', label: 'Снято', width: 70, render: r => (r.is_closed ? 'Да' : '—') },
  { key: 'remark_date', label: 'Дата замечания', width: 130, sortKey: 'remark_date', render: r => fmtDate(r.remark_date) },
  { key: 'answer_date', label: 'Дата ответа', width: 120, hidden: true, render: r => fmtDate(r.answer_date) },
];

const REMARK_FILTERS = [
  { key: 'sheet', label: 'Лист', type: 'list', multi: true, placeholder: 'Найти лист', emptyValue: 'none', emptyLabel: 'Без листа' },
  { key: 'section', label: 'Раздел', type: 'list', multi: true, placeholder: 'Найти раздел', emptyValue: 'none', emptyLabel: 'Без раздела' },
  { key: 'org', label: 'Экспертиза', type: 'list', multi: true, placeholder: 'Организация', emptyValue: 'none', emptyLabel: '—' },
  { key: 'responsible', label: 'Ответственный', type: 'list', multi: true, placeholder: 'Ответственный', emptyValue: 'none', emptyLabel: '—' },
  { key: 'closed', label: 'Снято', options: [{ value: 'closed', label: 'Снятые' }, { value: 'open', label: 'Открытые' }] },
];

// ЭКС-1c: «Архивные сторонние» — колонки без «Листа», с типом/наименованием объекта.
const EXTERNAL_COLUMNS = [
  { key: 'object_type', label: 'Тип объекта', width: 150, render: r => clip(r.object_type) },
  { key: 'object_name', label: 'Наименование', width: 240, render: r => clip(r.object_name) },
  { key: 'section_name', label: 'Раздел', width: 150, render: r => clip(r.section_name || r.section_raw) },
  { key: 'expertise_org', label: 'Экспертиза', width: 160, render: r => clip(r.expertise_org) },
  { key: 'expert', label: 'Эксперт', width: 140, render: r => clip(r.expert) },
  { key: 'remark_text', label: 'Замечание', width: 340, render: r => clip(r.remark_text) },
  { key: 'answer_text', label: 'Ответ', width: 260, render: r => clip(r.answer_text) },
  { key: 'is_closed', label: 'Снято', width: 70, render: r => (r.is_closed ? 'Да' : '—') },
  { key: 'remark_date', label: 'Дата замечания', width: 130, sortKey: 'remark_date', render: r => fmtDate(r.remark_date) },
  { key: 'answer_date', label: 'Дата ответа', width: 120, hidden: true, render: r => fmtDate(r.answer_date) },
];

// ЭКС-1c: свои фильтры сторонних — тип объекта и раздел (+ поиск); листа у них нет.
const EXTERNAL_FILTERS = [
  { key: 'object_type', label: 'Тип объекта', type: 'list', multi: true, placeholder: 'Тип объекта', emptyValue: 'none', emptyLabel: '—' },
  { key: 'section', label: 'Раздел', type: 'list', multi: true, placeholder: 'Найти раздел', emptyValue: 'none', emptyLabel: 'Без раздела' },
];

function remarkParams({ sort, filters, q }) {
  const p = { q, sort: sort || '-remark_date', bucket: _bucket };
  const join = (k) => (filters[k]?.length ? filters[k].join(',') : undefined);
  p.section = join('section');
  if (_bucket === 'external') {
    // у сторонних листа/ответственного нет — вместо листа фильтр по типу объекта
    p.object_type = join('object_type');
  } else {
    p.sheet = join('sheet');
    p.org = join('org');
    p.responsible = join('responsible');
    if (filters.closed?.[0]) p.closed = filters.closed[0];
    if (_showDrafts) p.drafts = 1;
  }
  return p;
}

async function fetchRemarkFacets(query) {
  const params = remarkParams(query);
  delete params.sort;
  const f = await api.expertise.remarkFacets(params);
  const counts = {};
  const listKeys = _bucket === 'external' ? ['object_type', 'section'] : ['sheet', 'section', 'org', 'responsible'];
  for (const key of listKeys) {
    counts[key] = Object.fromEntries(Object.entries(f.counts?.[key] ?? {}).map(([k, n]) => [k || 'none', n]));
  }
  if (_bucket !== 'external') counts.closed = f.counts?.closed ?? {};
  return { counts, summary: f.summary ?? '' };
}

function renderRemarks(body) {
  body.innerHTML = `
    <div class="expertise-subtabs" role="tablist">
      ${REMARK_BUCKETS.map(b => `<button type="button" role="tab" class="exp-subtab" data-exp-bucket="${b.key}">${b.label}</button>`).join('')}
    </div>
    <div class="expertise-remarks-host"></div>`;
  body.querySelector('.expertise-subtabs').addEventListener('click', (e) => {
    const t = e.target.closest?.('[data-exp-bucket]');
    if (t) { e.preventDefault(); setBucket(t.dataset.expBucket); }
  });
  setBucket(_bucket, true);
}

/** Переключение под-вкладки «Замечания»: сбросить таблицу/панель и перерисовать. */
function setBucket(bucket, force = false) {
  const wrap = _root.querySelector('.expertise-remarks-host');
  if (!wrap) return;
  if (bucket === _bucket && !force && wrap.children.length) return;
  closeRecordPanel();   // несохранённые правки при смене под-вкладки закрываем молча
  _bucket = bucket;
  _table = null;
  _root.querySelectorAll('[data-exp-bucket]').forEach(b => {
    b.classList.toggle('is-active', b.dataset.expBucket === bucket);
    b.setAttribute('aria-selected', String(b.dataset.expBucket === bucket));
  });
  // Свежий контейнер таблицы на каждое переключение — слушатели не копятся.
  wrap.innerHTML = '<div class="expertise-table"></div>';
  const host = wrap.firstElementChild;
  if (bucket === 'external') renderExternalRemarks(host);
  else renderOurRemarks(host);
}

/** Текущие / Архивные наши — обычная таблица замечаний (по нашим ИЛ). */
function renderOurRemarks(host) {
  const writer = canDo('expertise.write');
  _table = createDataTable(host, {
    storageKey: 'ccpm_dt_expertise_remarks',
    columns: REMARK_COLUMNS,
    filters: REMARK_FILTERS,
    actions: true,
    search: { placeholder: 'Текст замечания, ответ, ответственный' },
    pageSizes: [50, 100, 200],
    defaultSort: '-remark_date',
    fetch: (query) => api.expertise.remarks({ ...remarkParams(query), limit: query.limit, offset: query.offset }),
    facets: fetchRemarkFacets,
    onRowClick: (r) => openRemark(r),
    emptyHint: _bucket === 'archived_ours'
      ? 'Архивных замечаний (по закрытым листам) пока нет.'
      : 'Замечаний пока нет. Новые приходят файлом в карточку исполнительного листа.',
  });
  _table.setActions(
    `<label class="dt-closed"><input type="checkbox" data-exp-drafts${_showDrafts ? ' checked' : ''}> показать черновики</label>`
    + (writer ? '<button type="button" class="dt-btn" data-exp-upload>Загрузить замечания</button>' : '')
    + (writer ? '<button type="button" class="dt-btn cp-primary" data-exp-add>Добавить замечание</button>' : ''),
  );
  host.addEventListener('click', (e) => {
    if (writer && e.target.closest?.('[data-exp-add]')) { e.preventDefault(); openRemark(null); }
    if (writer && e.target.closest?.('[data-exp-upload]')) { e.preventDefault(); uploadRemarks(); }
  });
  host.addEventListener('change', (e) => {
    if (e.target.closest?.('[data-exp-drafts]')) { _showDrafts = e.target.checked; _table?.reload(); }
  });
}

/**
 * ЭКС-ЗАГРУЗКА-ПРОСТО: единый вход «Загрузить замечания» — ИЛ + Раздел по умолчанию +
 * файл(ы) (Excel, PDF, Word). Остальное (организация, эксперт, №, даты, текст, ответ,
 * статус, раздел каждого замечания) достаёт парсер/ИИ сам. Выбранный раздел применяется
 * к черновику ТОЛЬКО если парсер/ИИ раздел не определил — иначе остаётся определённый.
 * Пустой раздел = прежнее чистое ИИ-поведение.
 *
 * Табличный Excel формата 1С раскладывается сразу; PDF/многолистовой/нераспознанное
 * уходит в ИИ-очередь («ждёт ИИ-разбора»). Просмотр черновиков и подтверждение — в
 * карточке ИЛ («Входящие письма»), туда и переходим после загрузки.
 */
async function uploadRemarks() {
  const sections = await loadSections();
  // Раздел по умолчанию: плейсхолдер «— определит ИИ —» (не «без раздела» — раздел всё
  // равно определит парсер/ИИ; выбор здесь лишь fallback для неопределённых замечаний).
  const secOpts = ['<option value="">— определит ИИ —</option>']
    .concat((sections ?? []).map(s =>
      `<option value="${s.id}">${esc(s.code != null ? `${s.name} (${s.code})` : s.name)}</option>`))
    .join('');

  const dlg = document.createElement('dialog');
  dlg.className = 'app-modal';
  dlg.innerHTML = `
    <form method="dialog" class="modal-form">
      <h3 class="modal-title">Загрузить замечания</h3>
      <label class="modal-field modal-field-wide">
        <span class="modal-field-label">Исполнительный лист</span>
        <input type="hidden" name="sheet_id" value="">
        <span class="ref-link">
          <span class="ref-link-status" data-sheet-name>не выбран</span>
          <button type="button" class="dt-link" data-pick-sheet>из справочника…</button>
        </span>
      </label>
      <label class="modal-field modal-field-wide">
        <span class="modal-field-label">Раздел по умолчанию</span>
        <select name="section_id" class="modal-select">${secOpts}</select>
        <span class="modal-field-hint">Применится только к замечаниям, для которых раздел не определит сам парсер/ИИ.</span>
      </label>
      <label class="modal-field modal-field-wide">
        <span class="modal-field-label">Файл(ы) — Excel, PDF или Word</span>
        <input type="file" name="files" accept=".xlsx,.xls,.pdf,.docx,.doc" multiple required>
        <span class="modal-field-hint">ИИ сам разложит замечания по полям; затем проверите черновики в карточке листа.</span>
      </label>
      <div class="modal-actions">
        <button type="button" class="modal-cancel" data-cancel>Отмена</button>
        <button type="submit" class="modal-confirm" data-submit>Загрузить</button>
      </div>
    </form>`;
  document.body.appendChild(dlg);

  const sheetIdEl = dlg.querySelector('[name="sheet_id"]');
  const sheetNameEl = dlg.querySelector('[data-sheet-name]');
  let sheetName = '';
  const cleanup = () => { if (dlg.parentNode) dlg.remove(); };

  dlg.querySelector('[data-pick-sheet]').addEventListener('click', async () => {
    const picked = await pickSheet({
      title: 'К какому исполнительному листу привязать замечания?',
      currentId: sheetIdEl.value ? Number(sheetIdEl.value) : null,
    });
    if (picked?.action === 'select') {
      sheetIdEl.value = picked.id;
      sheetName = picked.name;
      sheetNameEl.textContent = picked.name;
      sheetNameEl.classList.add('is-linked');
    }
  });
  dlg.querySelector('[data-cancel]').addEventListener('click', () => dlg.close());
  dlg.addEventListener('close', cleanup, { once: true });

  dlg.querySelector('form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const sheetId = Number(sheetIdEl.value || 0);
    if (!sheetId) { showNotification('Сначала выберите исполнительный лист', 'error'); return; }
    const files = Array.from(dlg.querySelector('[name="files"]').files || []);
    if (!files.length) { showNotification('Выберите файл(ы)', 'error'); return; }
    const sectionId = dlg.querySelector('[name="section_id"]').value || undefined;
    const submitBtn = dlg.querySelector('[data-submit]');
    submitBtn.disabled = true;

    let queued = 0, drafts = 0, failed = 0;
    const errors = [];
    for (const file of files) {
      try {
        const res = await api.expertise.uploadSheetFile(sheetId, file, { section_id: sectionId });
        const st = res.file?.parse_status;
        if (st === 'queued') queued += 1;
        else if (st === 'done') drafts += res.drafts || 0;
        else failed += 1;
      } catch (err) {
        errors.push(`${file.name}: ${apiErrorText(err)}`);
      }
    }
    const parts = [];
    if (queued) parts.push(`ждут ИИ-разбора: ${queued}`);
    if (drafts) parts.push(`разобрано черновиков: ${drafts}`);
    if (failed) parts.push(`не разобрано: ${failed}`);
    if (errors.length) parts.push(errors.join('; '));
    const ok = queued + drafts > 0;
    showNotification(
      parts.length ? `Лист «${sheetName}»: ${parts.join(', ')}` : 'Файлы не загружены',
      ok ? 'info' : 'error',
      6000,
    );
    dlg.close();
    if (ok) router.push(`/projects/sheets?id=${sheetId}`);  // черновики и подтверждение — в карточке ИЛ
  });

  dlg.showModal();
}

/** Архивные сторонние — чужие замечания без ИЛ; свои колонки/фильтры/фасеты. */
function renderExternalRemarks(host) {
  const writer = canDo('expertise.write');
  _table = createDataTable(host, {
    storageKey: 'ccpm_dt_expertise_remarks_external',
    columns: EXTERNAL_COLUMNS,
    filters: EXTERNAL_FILTERS,
    actions: true,
    search: { placeholder: 'Текст замечания, ответ, наименование объекта' },
    pageSizes: [50, 100, 200],
    defaultSort: '-remark_date',
    fetch: (query) => api.expertise.remarks({ ...remarkParams(query), limit: query.limit, offset: query.offset }),
    facets: fetchRemarkFacets,
    onRowClick: (r) => openRemark(r),
    emptyHint: writer
      ? 'Сторонних замечаний пока нет. Добавьте вручную кнопкой «Добавить стороннее замечание».'
      : 'Сторонних замечаний пока нет.',
  });
  if (writer) {
    _table.setActions('<button type="button" class="dt-btn cp-primary" data-exp-add-external>Добавить стороннее замечание</button>');
    host.addEventListener('click', (e) => {
      if (e.target.closest?.('[data-exp-add-external]')) { e.preventDefault(); openRemark(null, { createExternal: true }); }
    });
  }
}

function sectionOptions(selectedId) {
  const opts = [{ value: '', label: '— без раздела' }].concat(
    (_sections ?? []).map(s => ({ value: String(s.id), label: s.code != null ? `${s.name} (${s.code})` : s.name })),
  );
  return opts.map(o => ({ ...o, value: o.value, label: o.label, _sel: String(selectedId ?? '') === o.value }));
}

/**
 * Замечание в правой панели записи (УИ-2a): правка/создание (expertise.write)
 * или просмотр. r=null — создание. Лист обычного замечания при создании обязателен
 * и задаётся только тут (ExpertiseRemarkUpdate не принимает project_sheet_id).
 *
 * ЭКС-1c: стороннее замечание (createExternal или r.source==='external') — БЕЗ ИЛ:
 * вместо листа «тип объекта»/«наименование объекта»; payload без project_sheet_id
 * (backend создаёт source='external'); «ответственный» не показываем.
 */
async function openRemark(r, { createExternal = false } = {}) {
  const writer = canDo('expertise.write');
  const isCreate = !r;
  const isExternal = createExternal || (!!r && r.source === 'external');

  // Просмотр (без права write) — панель только для чтения, поля в блоке вывода.
  if (!writer) {
    openRecordPanel(r, {
      title: `Замечание ${r.remark_no ?? ''}`.trim(),
      readonly: true,
      fields: [],
      readLines: [
        isExternal
          ? { label: 'Тип объекта', value: r.object_type }
          : { label: 'Исполнительный лист', value: r.sheet_short_name || (r.project_sheet_id ? `лист #${r.project_sheet_id}` : '—') },
        ...(isExternal ? [{ label: 'Наименование объекта', value: r.object_name }] : []),
        { label: 'Раздел', value: r.section_name || r.section_raw },
        { label: 'Организация экспертизы', value: r.expertise_org },
        { label: 'Эксперт', value: r.expert },
        { label: '№ замечания', value: r.remark_no },
        { label: 'Дата замечания', value: fmtDate(r.remark_date) },
        { label: 'Дата ответа', value: fmtDate(r.answer_date) },
        { label: 'Текст замечания', value: r.remark_text },
        { label: 'Ответ', value: r.answer_text },
        { label: 'Разъяснение', value: r.explanation },
        ...(isExternal ? [] : [{ label: 'Ответственный', value: `${r.responsible ?? '—'}${r.responsible_user_name ? ` (${r.responsible_user_name})` : ''}` }]),
        { label: 'Снято', value: r.is_closed ? 'да' : 'нет' },
      ],
      onClose: () => _table?.setSelected(null),
    });
    _table?.setSelected(r.id);
    return;
  }

  await loadSections();
  if (!isExternal) await loadUsers();

  // Обычное создание требует листов (прежнее поведение); стороннее — нет.
  let createSheets = null;
  if (isCreate && !isExternal) {
    try { createSheets = (await api.expertise.sheets({ limit: 500 })).items; } catch { createSheets = []; }
    if (!createSheets.length) {
      showNotification('Сначала импортируйте исполнительные листы из 1С', 'error');
      return;
    }
  }

  const buildFields = (rec) => {
    // ── Стороннее замечание: без «Исполнительного листа», с объектом и ручным разделом ──
    if (isExternal) {
      return [
        { name: 'object_type', label: 'Тип объекта', value: rec?.object_type ?? '' },
        { name: 'object_name', label: 'Наименование объекта', required: true, value: rec?.object_name ?? '' },
        { name: 'section', label: 'Раздел (справочник)', type: 'select', options: sectionOptions(rec?.section_id), value: String(rec?.section_id ?? '') },
        { name: 'section_raw', label: 'Раздел (если нет в справочнике)', value: rec?.section_raw ?? '',
          hint: 'Заполняется, если раздел не выбран из справочника' },
        { name: 'remark_no', label: '№ замечания', type: 'number', value: rec?.remark_no ?? '' },
        { name: 'expertise_org', label: 'Организация экспертизы', type: 'ref',
          value: rec?.expertise_org ?? '', refId: rec?.expertise_org_id ?? null,
          picker: (currentId) => pickCounterparty({ role: 'expertise', title: 'Организация экспертизы', currentId }) },
        { name: 'expert', label: 'Эксперт', value: rec?.expert ?? '' },
        { name: 'remark_date', label: 'Дата замечания', type: 'date', value: rec?.remark_date ? String(rec.remark_date).slice(0, 10) : '' },
        { name: 'answer_date', label: 'Дата ответа', type: 'date', value: rec?.answer_date ? String(rec.answer_date).slice(0, 10) : '' },
        { name: 'remark_text', label: 'Текст замечания', type: 'textarea', rows: 4, wide: true, value: rec?.remark_text ?? '' },
        { name: 'answer_text', label: 'Ответ', type: 'textarea', rows: 4, wide: true, value: rec?.answer_text ?? '' },
        { name: 'explanation', label: 'Разъяснение', type: 'textarea', rows: 3, wide: true, value: rec?.explanation ?? '' },
        { name: 'is_closed', label: 'Снято', type: 'select', value: rec?.is_closed ? '1' : '0',
          options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да' }] },
      ];
    }
    // ── Обычное замечание (как было) ──
    const fields = [];
    if (isCreate) {
      fields.push({ name: 'sheet', label: 'Исполнительный лист', type: 'select', required: true,
        options: createSheets.map(s => ({ value: String(s.id), label: s.short_name || s.onec_key })) });
    }
    fields.push(
      { name: 'section', label: 'Раздел', type: 'select', options: sectionOptions(rec?.section_id), value: String(rec?.section_id ?? '') },
      { name: 'remark_no', label: '№ замечания', type: 'number', value: rec?.remark_no ?? '' },
      { name: 'expertise_org', label: 'Организация экспертизы', type: 'ref',
        value: rec?.expertise_org ?? '', refId: rec?.expertise_org_id ?? null,
        picker: (currentId) => pickCounterparty({ role: 'expertise', title: 'Организация экспертизы', currentId }) },
      { name: 'expert', label: 'Эксперт', value: rec?.expert ?? '' },
      { name: 'remark_date', label: 'Дата замечания', type: 'date', value: rec?.remark_date ? String(rec.remark_date).slice(0, 10) : '' },
      { name: 'answer_date', label: 'Дата ответа', type: 'date', value: rec?.answer_date ? String(rec.answer_date).slice(0, 10) : '' },
      { name: 'remark_text', label: 'Текст замечания', type: 'textarea', rows: 4, wide: true, value: rec?.remark_text ?? '' },
      { name: 'answer_text', label: 'Ответ', type: 'textarea', rows: 4, wide: true, value: rec?.answer_text ?? '' },
      { name: 'explanation', label: 'Разъяснение', type: 'textarea', rows: 3, wide: true, value: rec?.explanation ?? '' },
      { name: 'responsible', label: 'Ответственный (текст)', value: rec?.responsible ?? '' },
      { name: 'responsible_user', label: 'Ответственный — пользователь', type: 'select',
        value: String(rec?.responsible_user_id ?? ''), options: userOptions() },
      { name: 'is_closed', label: 'Снято', type: 'select', value: rec?.is_closed ? '1' : '0',
        options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да' }] },
    );
    return fields;
  };

  const extTitle = isCreate
    ? 'Новое стороннее замечание'
    : `Стороннее замечание ${r.remark_no ?? ''} — ${r.object_name ?? ''}`.trim();
  const ourTitle = isCreate
    ? 'Новое замечание'
    : `Замечание ${r.remark_no ?? ''} — ${r.sheet_short_name ?? ''}`.trim();

  openRecordPanel(r, {
    title: isExternal ? extTitle : ourTitle,
    fields: buildFields,
    // Лист обычного замечания при правке только для вывода (апдейт его не принимает);
    // у стороннего листа нет.
    readLines: (isCreate || isExternal) ? [] : [
      { label: 'Исполнительный лист', value: r.sheet_short_name || (r.project_sheet_id ? `лист #${r.project_sheet_id}` : '—') },
    ],
    saveLabel: isCreate ? 'Добавить' : 'Сохранить',
    onSave: (v, { isCreate: create }) => {
      if (isExternal) {
        const hasSection = !!v.section;
        const payload = {
          object_type: v.object_type || null,
          object_name: v.object_name,
          section_id: hasSection ? Number(v.section) : null,
          section_raw: hasSection ? null : (v.section_raw || null),
          remark_no: v.remark_no == null || v.remark_no === '' ? null : Number(v.remark_no),
          expertise_org: v.expertise_org,
          expertise_org_id: v.expertise_org_id ?? null,
          expert: v.expert,
          remark_date: v.remark_date,
          answer_date: v.answer_date,
          remark_text: v.remark_text,
          answer_text: v.answer_text,
          explanation: v.explanation,
          is_closed: v.is_closed === '1',
        };
        // payload БЕЗ project_sheet_id → backend создаёт стороннее (source='external')
        return create ? api.expertise.createRemark(payload) : api.expertise.patchRemark(r.id, payload);
      }
      const payload = {
        section_id: v.section ? Number(v.section) : null,
        remark_no: v.remark_no == null ? null : Number(v.remark_no),
        expertise_org: v.expertise_org,
        expertise_org_id: v.expertise_org_id ?? null,
        expert: v.expert,
        remark_date: v.remark_date,
        answer_date: v.answer_date,
        remark_text: v.remark_text,
        answer_text: v.answer_text,
        explanation: v.explanation,
        responsible: v.responsible,
        responsible_user_id: v.responsible_user ? Number(v.responsible_user) : null,
        is_closed: v.is_closed === '1',
      };
      return create
        ? api.expertise.createRemark({ project_sheet_id: Number(v.sheet), ...payload })
        : api.expertise.patchRemark(r.id, payload);
    },
    reload: (saved) => api.expertise.getRemark(saved.id),
    onSaved: (_fresh, { isCreate: create }) => {
      showNotification(create ? 'Замечание добавлено' : 'Замечание сохранено', 'info');
      _table?.reload();
    },
    onClose: () => _table?.setSelected(null),
  });
  _table?.setSelected(r?.id ?? null);
}

// ── Вкладка «Разрешения на изменения» (ЭКС-1b) ─────────────────────────────────

const PERMIT_COLUMNS = [
  { key: 'permit_no', label: '№', width: 90, sortKey: 'permit_no', render: p => esc(p.permit_no ?? '—') },
  { key: 'permit_date', label: 'Дата', width: 110, sortKey: 'permit_date', render: p => fmtDate(p.permit_date) },
  { key: 'sheet', label: 'Лист', width: 240, render: p => clip(p.sheet_short_name || p.short_name) },
  { key: 'essence', label: 'Суть изменений', width: 320, render: p => clip(p.essence) },
  { key: 'change_code', label: 'Код изменения', width: 160, render: p => esc(CHANGE_CODE_LABEL[p.change_code] ?? (p.change_code_raw || '—')) },
  { key: 'gip', label: 'ГИП', width: 160, render: p => clip(p.gip_user_name || p.gip) },
  { key: 'author', label: 'Автор', width: 160, render: p => clip(p.author_user_name || p.author) },
  { key: 'incoming_letter', label: 'Вх. письмо', width: 120, hidden: true, render: p => esc(p.incoming_letter ?? '—') },
  { key: 'permit_date_out', label: 'Изменено', width: 120, hidden: true, sortKey: 'updated_at', render: p => fmtDate(p.updated_at) },
];

const PERMIT_FILTERS = [
  { key: 'sheet', label: 'Лист', type: 'list', multi: true, placeholder: 'Найти лист', emptyValue: 'none', emptyLabel: 'Без листа' },
  { key: 'change_code', label: 'Код изменения', multi: true, options: [
    { value: 'error_fix', label: 'Устранение ошибок' },
    { value: 'extra_requirements', label: 'Доп. требования' },
    { value: 'other', label: 'Другое' },
  ] },
  { key: 'gip', label: 'ГИП', type: 'list', multi: true, placeholder: 'ГИП', emptyValue: 'none', emptyLabel: '—' },
  { key: 'author', label: 'Автор', type: 'list', multi: true, placeholder: 'Автор', emptyValue: 'none', emptyLabel: '—' },
];

function permitParams({ sort, filters, q }) {
  const p = { q, sort: sort || '-permit_date' };
  const join = (k) => (filters[k]?.length ? filters[k].join(',') : undefined);
  p.sheet = join('sheet');
  p.change_code = join('change_code');
  p.gip = join('gip');
  p.author = join('author');
  return p;
}

async function fetchPermitFacets(query) {
  const params = permitParams(query);
  delete params.sort;
  const f = await api.expertise.permitFacets(params);
  const counts = {};
  for (const key of ['sheet', 'gip', 'author']) {
    counts[key] = Object.fromEntries(Object.entries(f.counts?.[key] ?? {}).map(([k, n]) => [k || 'none', n]));
  }
  counts.change_code = f.counts?.change_code ?? {};
  return { counts, summary: f.summary ?? '' };
}

function renderPermits(body) {
  body.innerHTML = '<div class="expertise-table"></div>';
  _table = createDataTable(body.firstElementChild, {
    storageKey: 'ccpm_dt_expertise_permits',
    columns: PERMIT_COLUMNS,
    filters: PERMIT_FILTERS,
    actions: canDo('expertise.write'),
    search: { placeholder: 'Суть, лист, письмо, №, ФИО' },
    pageSizes: [50, 100, 200],
    defaultSort: '-permit_date',
    fetch: (query) => api.expertise.permits({ ...permitParams(query), limit: query.limit, offset: query.offset }),
    facets: fetchPermitFacets,
    onRowClick: (p) => openPermit(p),
    emptyHint: canDo('expertise.write')
      ? 'Разрешений пока нет. Создайте вручную кнопкой «Создать разрешение» (импорт выгрузки 1С — через CLI).'
      : 'Разрешений пока нет.',
  });
  if (canDo('expertise.write')) {
    _table.setActions('<button type="button" class="dt-btn cp-primary" data-exp-add-permit>Создать разрешение</button>');
    body.firstElementChild.addEventListener('click', (e) => {
      if (e.target.closest?.('[data-exp-add-permit]')) { e.preventDefault(); openPermit(null); }
    });
  }
}

/**
 * Разрешение на изменение в правой панели (УИ-2a): правка/создание
 * (expertise.write) или просмотр. p=null — создание. Удаление — кнопка
 * «Удалить» в футере с подтверждением (вместо прежнего select-костыля).
 */
async function openPermit(p) {
  const writer = canDo('expertise.write');
  const isCreate = !p;

  // Просмотр (без права write) — панель только для чтения.
  if (!writer) {
    openRecordPanel(p, {
      title: `Разрешение ${p.permit_no ?? ''}`.trim(),
      readonly: true,
      fields: [],
      readLines: [
        { label: '№ разрешения', value: p.permit_no },
        { label: 'Дата', value: fmtDate(p.permit_date) },
        { label: 'Исполнительный лист', value: p.sheet_short_name || p.short_name },
        { label: 'Наименование (текст из 1С)', value: p.short_name },
        { label: 'Суть изменений', value: p.essence },
        { label: 'Входящее письмо', value: p.incoming_letter },
        { label: 'Код изменения', value: CHANGE_CODE_LABEL[p.change_code] ?? (p.change_code_raw || '—') },
        { label: 'ГИП', value: `${p.gip ?? '—'}${p.gip_user_name ? ` (${p.gip_user_name})` : ''}` },
        { label: 'Автор', value: `${p.author ?? '—'}${p.author_user_name ? ` (${p.author_user_name})` : ''}` },
      ],
      onClose: () => _table?.setSelected(null),
    });
    _table?.setSelected(p.id);
    return;
  }

  await loadUsers();
  let sheets = [];
  try { sheets = (await api.expertise.sheets({ limit: 500 })).items; } catch { /* пусто */ }
  const sheetOpts = [{ value: '', label: '— не связан с листом' }]
    .concat(sheets.map(s => ({ value: String(s.id), label: s.short_name || s.onec_key })));

  const buildFields = (rec) => [
    { name: 'permit_no', label: '№ разрешения', value: rec?.permit_no ?? '' },
    { name: 'permit_date', label: 'Дата', type: 'date', value: rec?.permit_date ? String(rec.permit_date).slice(0, 10) : '' },
    { name: 'sheet', label: 'Исполнительный лист', type: 'select', value: String(rec?.project_sheet_id ?? ''), options: sheetOpts },
    { name: 'short_name', label: 'Наименование (текст из 1С)', value: rec?.short_name ?? '' },
    { name: 'essence', label: 'Суть изменений', type: 'textarea', rows: 3, wide: true, value: rec?.essence ?? '' },
    { name: 'incoming_letter', label: 'Входящее письмо', value: rec?.incoming_letter ?? '' },
    { name: 'change_code', label: 'Код изменения', type: 'select', value: rec?.change_code ?? '',
      options: [{ value: '', label: '— не указан' },
        { value: 'error_fix', label: 'Устранение ошибок' },
        { value: 'extra_requirements', label: 'Доп. требования' },
        { value: 'other', label: 'Другое' }] },
    { name: 'gip', label: 'ГИП (текст)', value: rec?.gip ?? '' },
    { name: 'gip_user', label: 'ГИП — пользователь', type: 'select', value: String(rec?.gip_user_id ?? ''), options: userOptions() },
    { name: 'author', label: 'Автор (текст)', value: rec?.author ?? '' },
    { name: 'author_user', label: 'Автор — пользователь', type: 'select', value: String(rec?.author_user_id ?? ''), options: userOptions() },
  ];

  openRecordPanel(p, {
    title: isCreate ? 'Новое разрешение на изменение' : `Разрешение ${p.permit_no ?? ''}`.trim(),
    fields: buildFields,
    saveLabel: isCreate ? 'Создать' : 'Сохранить',
    onSave: (v, { isCreate: create }) => {
      const payload = {
        permit_no: v.permit_no,
        permit_date: v.permit_date,
        project_sheet_id: v.sheet ? Number(v.sheet) : null,
        short_name: v.short_name,
        essence: v.essence,
        incoming_letter: v.incoming_letter,
        change_code: v.change_code || null,
        gip: v.gip,
        author: v.author,
        gip_user_id: v.gip_user ? Number(v.gip_user) : null,
        author_user_id: v.author_user ? Number(v.author_user) : null,
      };
      return create ? api.expertise.createPermit(payload) : api.expertise.patchPermit(p.id, payload);
    },
    // Одиночного GET разрешения нет — ответ PATCH/POST уже содержит свежую запись.
    onDelete: isCreate ? undefined : (() => api.expertise.deletePermit(p.id)),
    deleteMessage: `Разрешение ${p?.permit_no ?? ''} будет удалено без возможности восстановления.`.trim(),
    onSaved: (_fresh, { deleted, isCreate: create }) => {
      showNotification(deleted ? 'Разрешение удалено' : (create ? 'Разрешение создано' : 'Разрешение сохранено'), 'info');
      _table?.reload();
    },
    onClose: () => _table?.setSelected(null),
  });
  _table?.setSelected(p?.id ?? null);
}

// ── Вкладка «Статистика» (ЭКС-1b, expertise.stats) ─────────────────────────────

async function renderStats(body) {
  body.innerHTML = `
    <div class="expertise-stats">
      <form class="expertise-stats-period" data-stats-period>
        <label>Период с <input type="date" name="from"></label>
        <label>по <input type="date" name="to"></label>
        <button type="submit" class="dt-btn">Показать</button>
      </form>
      <div class="expertise-stats-body" data-stats-body><div class="loading-spinner">Загрузка…</div></div>
      ${canDo('expertise.write') ? '<div class="expertise-people" data-people-body></div>' : ''}
    </div>`;
  const form = body.querySelector('[data-stats-period]');
  form.addEventListener('submit', (e) => { e.preventDefault(); loadStats(body); });
  await loadStats(body);
  const peopleEl = body.querySelector('[data-people-body]');
  if (peopleEl) initPeople(peopleEl, body);
}

async function loadStats(body) {
  const form = body.querySelector('[data-stats-period]');
  const params = {};
  if (form.from.value) params.from = form.from.value;
  if (form.to.value) params.to = form.to.value;
  const el = body.querySelector('[data-stats-body]');
  el.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
  try {
    const s = await api.expertise.stats(params);
    el.innerHTML = renderStatsTables(s);
  } catch (e) {
    if (e.message === '401') return;
    el.innerHTML = `<p class="tenders-error">${esc(apiErrorText(e))}</p>`;
  }
}

const pct = (v) => `${Math.round((v ?? 0) * 100)}%`;

function renderStatsTables(s) {
  const sec = s.sections.map(r =>
    `<tr><td>${esc(r.name)}</td><td class="num">${r.remarks}</td><td class="num">${r.closed}</td><td class="num">${pct(r.closed_share)}</td></tr>`).join('')
    || '<tr><td colspan="4" class="ti-hint">нет данных за период</td></tr>';
  const personRows = (list) => (list.map(r =>
    `<tr><td>${esc(r.name)}${r.matched ? '' : ' <span class="ti-hint">(не связан)</span>'}</td><td class="num">${r.count}</td></tr>`).join('')
    || '<tr><td colspan="2" class="ti-hint">нет данных за период</td></tr>');
  return `
    <section class="dash-widget expertise-stats-widget">
      <header class="dash-widget-header"><h3>Замечания по разделам</h3></header>
      <table class="expertise-stats-table"><thead><tr><th>Раздел</th><th class="num">Замечаний</th><th class="num">Снято</th><th class="num">Доля снятых</th></tr></thead><tbody>${sec}</tbody></table>
    </section>
    <section class="dash-widget expertise-stats-widget">
      <header class="dash-widget-header"><h3>Замечания по ответственным</h3></header>
      <table class="expertise-stats-table"><thead><tr><th>Ответственный</th><th class="num">Замечаний</th></tr></thead><tbody>${personRows(s.responsible)}</tbody></table>
    </section>
    <section class="dash-widget expertise-stats-widget">
      <header class="dash-widget-header"><h3>Разрешения «Устранение ошибок» по ГИП</h3></header>
      <table class="expertise-stats-table"><thead><tr><th>ГИП</th><th class="num">Разрешений</th></tr></thead><tbody>${personRows(s.gip)}</tbody></table>
    </section>
    <section class="dash-widget expertise-stats-widget">
      <header class="dash-widget-header"><h3>Разрешения «Устранение ошибок» по авторам</h3></header>
      <table class="expertise-stats-table"><thead><tr><th>Автор</th><th class="num">Разрешений</th></tr></thead><tbody>${personRows(s.author)}</tbody></table>
    </section>`;
}

// ── Сопоставление ФИО с пользователями (панель во вкладке «Статистика») ─────────

const PEOPLE_FIELDS = [
  { key: 'responsible', label: 'Ответственные (замечания)' },
  { key: 'gip', label: 'ГИП (разрешения)' },
  { key: 'author', label: 'Авторы (разрешения)' },
];

/** Навесить обработчик привязки один раз и наполнить панель ФИО. */
function initPeople(el, statsBody) {
  el.addEventListener('change', async (e) => {
    const sel = e.target.closest?.('select[data-link-field]');
    if (!sel) return;
    const body = { field: sel.dataset.linkField, name: sel.dataset.linkName,
      user_id: sel.value ? Number(sel.value) : null };
    try {
      const res = await api.expertise.linkPerson(body);
      showNotification(`Связано строк: ${res.updated}`, 'info');
      await fillPeople(el);
      await loadStats(statsBody);  // статистика пересчитается по user_id
    } catch (err) {
      if (err.message !== '401') showNotification(apiErrorText(err), 'error');
    }
  });
  fillPeople(el);
}

async function fillPeople(el) {
  el.innerHTML = '<div class="loading-spinner">Загрузка ФИО…</div>';
  await loadUsers();
  let data;
  try { data = await api.expertise.people(); }
  catch (e) {
    if (e.message === '401') return;
    el.innerHTML = `<p class="tenders-error">${esc(apiErrorText(e))}</p>`;
    return;
  }
  el.innerHTML = `
    <section class="dash-widget expertise-people-widget">
      <header class="dash-widget-header"><h3>Сопоставление ФИО с пользователями</h3></header>
      <p class="ti-hint">Статистика по людям точнее, когда ФИО из 1С связаны с пользователями.
        Выбор пользователя связывает сразу все строки с этим ФИО (во всех написаниях).</p>
      ${PEOPLE_FIELDS.map(f => renderPeopleGroup(f, data[f.key] ?? [])).join('')}
    </section>`;
}

function renderPeopleGroup(f, rows) {
  if (!rows.length) {
    return `<div class="expertise-people-group"><h4>${esc(f.label)}</h4><p class="ti-hint">нет данных</p></div>`;
  }
  const userOpt = (sel) => (_users ?? []).map(u =>
    `<option value="${esc(String(u.id))}"${String(sel) === String(u.id) ? ' selected' : ''}>${esc(u.full_name)}</option>`).join('');
  const tr = rows.map(r => {
    const none = r.suggested_user_id ? `— не связан (похоже: ${esc(r.suggested_user_name)})` : '— не связан';
    const sel = `<select data-link-field="${esc(f.key)}" data-link-name="${esc(r.norm)}">
      <option value=""${r.user_id ? '' : ' selected'}>${none}</option>${userOpt(r.user_id)}</select>`;
    return `<tr><td>${esc(r.name)}</td><td class="num">${r.count}</td><td>${sel}</td></tr>`;
  }).join('');
  return `<div class="expertise-people-group"><h4>${esc(f.label)}</h4>
    <table class="expertise-stats-table"><thead><tr><th>ФИО из 1С</th><th class="num">Строк</th><th>Пользователь</th></tr></thead><tbody>${tr}</tbody></table></div>`;
}
